import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import type { GridItem } from '@/features/integrations/add-integrations.hooks';
import type { IntegrationTarget } from '@/features/integrations/integrations.types';
import { connectIntegration } from '@/integrations/integrations.utils';
import { trackEvent } from '@/utils/analytics';
import { inBrowser } from '@/utils/capabilities';

import type { OAuthReturnTo } from '@littlebird/mcp';

type LinkTarget = {
  displayName: string;
  isConnected: boolean;
  target: IntegrationTarget;
};

export const findLinkTarget = (
  items: GridItem[],
  slug: string
): LinkTarget | null => {
  for (const item of items) {
    if (item.type === 'mcp_catalog' && item.entry.id === slug) {
      const { entry } = item;
      return {
        displayName: entry.name,
        isConnected: false,
        target: {
          kind: 'mcp',
          catalogId: slug,
          displayName: entry.name,
          url: entry.url,
          requiresPaidPlan: entry.requires_paid_plan,
        },
      };
    }
    if (item.type === 'mcp_server' && item.server.slug === slug) {
      const { id, name } = item.server;
      // An account that is not working is reconnected; a fresh connect could add a second account beside it
      return {
        displayName: name,
        isConnected: item.isConnected,
        target: {
          kind: 'mcp',
          displayName: name,
          requiresPaidPlan: true,
          reconnectServerId: id,
        },
      };
    }
    if (
      item.type === 'integration' &&
      item.integration.info.toolkit_name === slug
    ) {
      const { info } = item.integration;
      return {
        displayName: info.display_name,
        isConnected: item.isConnected,
        target: {
          kind: 'integration',
          toolkitName: slug,
          displayName: info.display_name,
          requiresPaidPlan: info.requires_paid_plan,
        },
      };
    }
  }
  return null;
};

/** The app a link asks to connect, once the list it is looked up in has loaded and if not started yet */
const linkToStart = (
  connect: string | undefined,
  isLoading: boolean,
  started: string | null
): string | null =>
  connect && !isLoading && connect !== started ? connect : null;

const startLinkConnect = async (
  slug: string,
  match: LinkTarget,
  returnTo: OAuthReturnTo | undefined
): Promise<void> => {
  const sameTab = inBrowser();
  const properties = {
    integration: slug,
    source: sameTab ? 'email_link_web' : 'email_link',
  };
  if (match.isConnected) {
    trackEvent('INTEGRATION_LINK_OPENED', {
      ...properties,
      outcome: 'already_connected',
    });
    toast(`${match.displayName} is already connected`);
    return;
  }
  trackEvent('INTEGRATION_LINK_OPENED', { ...properties, outcome: 'prompted' });
  // Any site can open this link, so the connect starts from the user's click, not from the link
  const confirmed = await ModalsManager.showResolvable(ConfirmDialog, {
    title: `Connect ${match.displayName}?`,
    description: `${match.displayName} will ask you to approve access next.`,
    confirmText: 'Connect',
    confirmVariant: 'default',
  });
  if (!confirmed) {
    trackEvent('INTEGRATION_LINK_CONNECT_DISMISSED', properties);
    return;
  }
  trackEvent('INTEGRATION_LINK_CONNECT_STARTED', properties);
  void connectIntegration(match.target, `email_link:${slug}`, {
    returnTo,
    sameTab,
  });
};

/**
 * A link such as an email's `?connect=slack` asks to connect that app once it shows up in the loaded
 * integrations, and the user's Connect starts it the way the Home row's Connect does. `items` must be the
 * whole list, not the filtered grid, so a search or category left on the page cannot hide the app. The parameter is
 * cleared first, so Back or a reload does not ask again. In a browser the consent page opens in this tab.
 */
export function useConnectFromLink(
  items: GridItem[],
  isLoading: boolean,
  returnTo?: OAuthReturnTo
) {
  const { connect } = useSearch({ strict: false }) as { connect?: string };
  const navigate = useNavigate();
  // The cleared parameter only reaches us on a later render; this keeps a reload of `items` from starting twice
  const handled = useRef<string | null>(null);

  useEffect(() => {
    if (!connect) {
      // Once the parameter is gone the link is done, so the same link opened again, on a page that stayed
      // mounted, asks again
      handled.current = null;
      return;
    }
    const slug = linkToStart(connect, isLoading, handled.current);
    // MCP apps join the list only once their feature flag loads, which can be after the list says it has
    // loaded; an app not in it yet waits for the next list, and an unknown id is left alone
    const match = slug ? findLinkTarget(items, slug) : null;
    if (!slug || !match) return;
    handled.current = slug;
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        connect: undefined,
      }),
      replace: true,
    });
    void startLinkConnect(slug, match, returnTo);
  }, [connect, isLoading, items, navigate, returnTo]);
}
